import _ from 'lodash';
import moment from 'moment';

import React, {
    useCallback,
    useContext,
    useState
} from 'react';

import {
    useIsExporting,
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useSetExportConfig
} from '../../../hooks/exportConfigurationHooks.js';

import useMetrics from '../../../hooks/useMetrics.js';

import {
    useTranslation
} from '../../../../i18n.js';

import LandscapeContext from '../../../contexts/landscapeContext.js';

import MetricLoadingView from '../../../components/MetricLoadingView.jsx';
import MetricTableHeader from '../../../components/table/MetricTableHeader.jsx';
import Panel from '../../../components/Panel.jsx';
import RIQModal from '../../../components/RIQModal.jsx';

import {
    RIQPopover,
    RIQInfoPopover
} from '../../../components/RIQPopover.jsx';

import {
    useComputedCurationConfigValue,
    useCurationConfigState,
    useCurationConfigValue,
    useGetFocusedInstanceIdValue
} from '../../../hooks/widgetConfiguration.js';

function PresenceIcon({label, size, url}) {
    const [hasPresenceIconError, setHasPresenceIconError] = useState(false);

    const onPresenceIconError = useCallback(() => {
        setHasPresenceIconError(true);
    }, []);

    let dimension = 45;
    if (size === 'lg') {
        dimension = 58;
    }

    const dimensionClasses = `tlw-h-[${dimension}px] tlw-w-[${dimension}px]`;

    if (hasPresenceIconError) {
        return (
            <div className={`tlw-flex ${dimensionClasses} tlw-items-center tlw-justify-center tlw-rounded-sm tlw-bg-gray-300 tlw-text-center tlw-font-medium tlw-uppercase`}>{_.first(label)}</div>
        );
    }

    return (
        <img
            alt={label}
            className={`${dimensionClasses} tlw-overflow-hidden tlw-rounded-sm`}
            onError={onPresenceIconError}
            src={url}
        />
    );
}

function Media({className, url}) {
    const [hasMediaError, setHasMediaError] = useState(false);
    const [hasMediaLoaded, setHasMediaLoaded] = useState(false);

    const onMediaError = useCallback(() => {
        setHasMediaError(true);
    }, []);

    const onMediaLoad = useCallback(() => {
        setHasMediaLoaded(true);
    }, []);

    if (!url || hasMediaError) {
        return;
    }

    let classNames = 'tlw-overflow-hidden tlw-rounded-sm';
    if (!hasMediaLoaded) {
        classNames = `${classNames} tlw-hidden`;
    }

    if (className) {
        classNames = `${classNames} ${className}`;
    }

    return (
        <img
            alt='post media'
            className={classNames}
            onError={onMediaError}
            onLoad={onMediaLoad}
            src={url} />
    );
}

function PostCell({companyId, data}) {
    const isExporting = useIsExporting();

    let footer = null;
    if (!isExporting) {
        footer = (
            <a
                className='tlw-text-sm tlw-text-gray-700'
                href={_.last(data.post_url)}
                rel='noreferrer'
                target='blank'>
                <i className='fa fa-fw fa-external-link'></i> View on TikTok
            </a>
        );
    }

    return (
        <td>
            <article
                className='tlw-flex tlw-gap-4'>
                <div
                    className='tlw-flex tlw-flex-col tlw-gap-2 tlw-py-4 tlw-pl-4'>
                    <PresenceIcon
                        label={_.last(data.company_name)}
                        url={_.last(data.presence_icon_url)}
                    />
                    <div
                        className='tlw-rounded tlw-bg-gray-700 tlw-px-4 tlw-py-1 tlw-text-center tlw-text-[10px] tlw-font-bold tlw-text-white'>
                        {_.last(data.media_type)}
                    </div>
                </div>
                <section
                    className='tlw-grow tlw-px-2 tlw-py-4'>
                    <header>
                        <div>
                            <a href={`/company/${companyId}`}>{_.last(data.company_name)}</a>
                        </div>
                    </header>
                    <main className='tlw-py-4'>{_.last(data.message)}</main>
                    <footer>{footer}</footer>
                </section>
                <Media
                    className='tlw-m-4 tlw-max-h-[100px] tlw-max-w-[120px]'
                    url={_.last(data.media_url_persisted)}
                />
            </article>
        </td>
    );
}

function PublishedAtCell({data, timezone = 'UTC'}) {
    const value = _.last(data);
    const publishedAt = moment.tz(value, moment.ISO_8601, timezone);

    return (
        <td
            className='tlw-min-w-[108px] tlw-p-4 tlw-text-center'
        >
            <div
                className='tlw-whitespace-nowrap tlw-text-md tlw-font-bold'>
                {publishedAt.format('MMM D, YYYY')}
            </div>
            <div
                className='tlw-text-sm tlw-text-gray-700'>
                {publishedAt.format('h:mm z')}
            </div>
        </td>
    );
}

function getMetricData(request, result) {
    return {
        raw: _.last(_.last(result.data)),
        value: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.expanded))),
        formatted: _.last(_.last(RivalIQ.metrics.new.MetricFormatter.getFormattedResults(request, result, RivalIQ.metrics.new.enums.MetricFormatTypes.abbreviated)))
    };
}

function MetricCell({data}) {
    return (
        <td
            className='tlw-min-w-[108px] tlw-p-4 tlw-text-right'
        >
            {data._metric.formatted}
        </td>
    );
}


const createTikTokPostResourceLocator = (t) => {
    return (measure, key) => {
        const path = [RivalIQ.enums.sources.TIKTOK_INSIGHTS, measure, 'post_id', key].join('.');
        return t(`${path}`);
    };
};

function PostDetailsModal({companyId, isModalVisible, metricResults, onModalDismissed, timezone}) {
    const {
        t: tMetrics
    } = useTranslation('metrics');

    const getTikTokInsightsResource = createTikTokPostResourceLocator(tMetrics);

    if (_.isEmpty(metricResults)) {
        return;
    }

    const publishedAt = moment.tz(_.last(metricResults.published_at), moment.ISO_8601, timezone);

    return (
        <RIQModal
            title='Post Details'
            showModal={isModalVisible}
            onModalDismissed={onModalDismissed}>
            <div
                className='tlw-flex tlw-w-full'>
                <div
                    className='tlw-flex tlw-grow tlw-gap-7.5 tlw-p-7.5'>
                    <article
                        className='tlw-flex tlw-gap-4'>
                        <div
                            className='tlw-flex tlw-flex-col tlw-gap-2 tlw-p-2'>
                            <PresenceIcon
                                label={_.last(metricResults.company_name)}
                                url={_.last(metricResults.presence_icon_url)}
                                size='lg'
                            />
                            <span
                                className='tlw-rounded tlw-bg-gray-700 tlw-px-4 tlw-py-1 tlw-text-center tlw-text-[10px] tlw-font-bold tlw-text-white'>
                                {_.last(metricResults.media_type)}
                            </span>
                        </div>
                        <section
                            className='tlw-p-2'>
                            <header>
                                <div>
                                    <a href={`/company/${companyId}`}>{_.last(metricResults.company_name)}</a>
                                </div>
                                <div className='tlw-text-sm tlw-text-gray-700'>{publishedAt.format('MMM D, YYYY h:mm A z')}</div>
                            </header>
                            <main className='tlw-py-4'>
                                <div>
                                    {_.last(metricResults.message)}
                                </div>
                                <Media
                                    className='tlw-mb-4 tlw-mt-6'
                                    url={_.last(metricResults.media_url_persisted)}
                                />
                            </main>
                            <footer>
                                <a
                                    className='tlw-text-sm tlw-text-gray-700'
                                    href={_.last(metricResults.post_url)}
                                    rel='noreferrer'
                                    target='blank'>
                                    <i className='fa fa-fw fa-external-link'></i> View on TikTok
                                </a>
                            </footer>
                        </section>
                    </article>
                </div>

                <div className='tlw-border-0 tlw-border-l tlw-border-solid tlw-border-gray-300 tlw-bg-gray-125'>

                    <div className='tlw-table tlw-border-collapse'>
                        <div className='tlw-table-row'>
                            <div className={`tlw-table-cell tlw-pl-7 tlw-pt-7 tlw-text-right tlw-align-middle tlw-text-21 tlw-font-bold`}>
                                <RIQPopover
                                    triggerNode={metricResults.engagement_total._metric.formatted}
                                    placement='top'
                                >
                                    <div className='tlw-px-7 tlw-py-4.5'>
                                        <div
                                            className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                            {metricResults.engagement_total._metric.value}
                                        </div>
                                    </div>
                                </RIQPopover>
                            </div>
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-pt-7 tlw-align-middle tlw-text-sm tlw-font-bold tlw-text-gray-900'>
                                <span className='tlw-uppercase'>{getTikTokInsightsResource('engagement_total', 'name')}</span>
                                <RIQInfoPopover
                                    title={getTikTokInsightsResource('engagement_total', 'name')}
                                    description={getTikTokInsightsResource('engagement_total', 'description')}
                                />
                            </div>
                        </div>
                        <div className='tlw-table-row tlw-text-sm'>
                            <div className={`tlw-table-cell tlw-text-right tlw-align-middle`}>
                                <RIQPopover
                                    triggerNode={metricResults.likes._metric.formatted}
                                    placement='top'
                                >
                                    <div className='tlw-px-7 tlw-py-4.5'>
                                        <div
                                            className='tlw-leading-36 tlw-text-gray-900'>
                                            {metricResults.likes._metric.value}
                                        </div>
                                    </div>
                                </RIQPopover>
                            </div>
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-align-middle tlw-text-gray-900'>
                                <span className='tlw-uppercase'>{getTikTokInsightsResource('likes', 'name')}</span>
                                <RIQInfoPopover
                                    title={getTikTokInsightsResource('likes', 'name')}
                                    description={getTikTokInsightsResource('likes', 'description')}
                                />
                            </div>
                        </div>
                        <div className='tlw-table-row tlw-text-sm'>
                            <div className={`tlw-table-cell tlw-text-right tlw-align-middle`}>
                                <RIQPopover
                                    triggerNode={metricResults.comments._metric.formatted}
                                    placement='top'
                                >
                                    <div className='tlw-px-7 tlw-py-4.5'>
                                        <div
                                            className='tlw-leading-36 tlw-text-gray-900'>
                                            {metricResults.comments._metric.value}
                                        </div>
                                    </div>
                                </RIQPopover>
                            </div>
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-align-middle tlw-text-gray-900'>
                                <span className='tlw-uppercase'>{getTikTokInsightsResource('comments', 'name')}</span>
                                <RIQInfoPopover
                                    title={getTikTokInsightsResource('comments', 'name')}
                                    description={getTikTokInsightsResource('comments', 'description')}
                                />
                            </div>
                        </div>
                        <div className='tlw-table-row tlw-text-sm'>
                            <div className={`tlw-table-cell tlw-pb-7 tlw-pl-7 tlw-text-right tlw-align-middle`}>
                                <RIQPopover
                                    triggerNode={metricResults.shares._metric.formatted}
                                    placement='top'
                                >
                                    <div className='tlw-px-7 tlw-py-4.5'>
                                        <div
                                            className='tlw-leading-36 tlw-text-gray-900'>
                                            {metricResults.shares._metric.value}
                                        </div>
                                    </div>
                                </RIQPopover>
                            </div>
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-pb-7 tlw-align-middle tlw-text-gray-900'>
                                <span className='tlw-uppercase'>{getTikTokInsightsResource('shares', 'name')}</span>
                                <RIQInfoPopover
                                    title={getTikTokInsightsResource('shares', 'name')}
                                    description={getTikTokInsightsResource('shares', 'description')}
                                />
                            </div>
                        </div>
                        <div className='tlw-table-row tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300'>
                            <div className={`tlw-table-cell tlw-py-7 tlw-pl-7 tlw-text-right tlw-align-middle tlw-text-21 tlw-font-bold`}>
                                <RIQPopover
                                    triggerNode={metricResults.reach._metric.formatted}
                                    placement='top'
                                >
                                    <div className='tlw-px-7 tlw-py-4.5'>
                                        <div
                                            className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                            {metricResults.reach._metric.value}
                                        </div>
                                    </div>
                                </RIQPopover>
                            </div>
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-align-middle tlw-text-sm tlw-font-bold tlw-text-gray-900'>
                                <span className='tlw-uppercase'>{getTikTokInsightsResource('reach', 'name')}</span>
                                <RIQInfoPopover
                                    title={getTikTokInsightsResource('reach', 'name')}
                                    description={getTikTokInsightsResource('reach', 'description')}
                                />
                            </div>
                        </div>
                        <div className='tlw-table-row tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300'>
                            <div className={`tlw-table-cell tlw-py-7 tlw-pl-7 tlw-text-right tlw-align-middle tlw-text-21 tlw-font-bold`}>
                                <RIQPopover
                                    triggerNode={metricResults.engagement_rate_by_presence_reach._metric.formatted}
                                    placement='top'
                                >
                                    <div className='tlw-px-7 tlw-py-4.5'>
                                        <div
                                            className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                            {metricResults.engagement_rate_by_presence_reach._metric.value}
                                        </div>
                                    </div>
                                </RIQPopover>
                            </div>
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-align-middle tlw-text-sm tlw-font-bold tlw-text-gray-900'>
                                <span className='tlw-uppercase'>{getTikTokInsightsResource('engagement_rate_by_presence_reach', 'name')}</span>
                                <RIQInfoPopover
                                    title={getTikTokInsightsResource('engagement_rate_by_presence_reach', 'name')}
                                    description={getTikTokInsightsResource('engagement_rate_by_presence_reach', 'description')}
                                />
                            </div>
                        </div>
                        <div className='tlw-table-row tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300'>
                            <div className={`tlw-table-cell tlw-py-7 tlw-pl-7 tlw-text-right tlw-align-middle tlw-text-21 tlw-font-bold`}>
                                <RIQPopover
                                    triggerNode={metricResults.engagement_rate_by_view._metric.formatted}
                                    placement='top'
                                >
                                    <div className='tlw-px-7 tlw-py-4.5'>
                                        <div
                                            className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                            {metricResults.engagement_rate_by_view._metric.value}
                                        </div>
                                    </div>
                                </RIQPopover>
                            </div>
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-align-middle tlw-text-sm tlw-font-bold tlw-text-gray-900'>
                                <span className='tlw-uppercase'>{getTikTokInsightsResource('engagement_rate_by_view', 'name')}</span>
                                <RIQInfoPopover
                                    title={getTikTokInsightsResource('engagement_rate_by_view', 'name')}
                                    description={getTikTokInsightsResource('engagement_rate_by_view', 'description')}
                                />
                            </div>
                        </div>
                        <div className='tlw-table-row tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-300'>
                            <div className={`tlw-table-cell tlw-py-7 tlw-pl-7 tlw-text-right tlw-align-middle tlw-text-21 tlw-font-bold`}>
                                <RIQPopover
                                    triggerNode={metricResults.video_views._metric.formatted}
                                    placement='top'
                                >
                                    <div className='tlw-px-7 tlw-py-4.5'>
                                        <div
                                            className='tlw-text-xl tlw-font-bold tlw-leading-36 tlw-text-gray-900'>
                                            {metricResults.video_views._metric.value}
                                        </div>
                                    </div>
                                </RIQPopover>
                            </div>
                            <div className='tlw-table-cell tlw-whitespace-nowrap tlw-px-8 tlw-align-middle tlw-text-sm tlw-font-bold tlw-text-gray-900'>
                                <span className='tlw-uppercase'>{getTikTokInsightsResource('video_views', 'name')}</span>
                                <RIQInfoPopover
                                    title={getTikTokInsightsResource('video_views', 'name')}
                                    description={getTikTokInsightsResource('video_views', 'description')}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </RIQModal>
    );
}

function PostsFooterContext({metrics}) {
    const received = _.size(metrics.received.data);
    const total = RivalIQ.formatter.formatAbbreviated(_.last(_.last(metrics.total.data)));
    return (
        <span className='tlw-self-center tlw-text-sm'>Displaying <strong>{received}</strong> of <strong>{total}</strong> posts <RIQInfoPopover description={'Rival IQ displays a max of 200 posts. To view other posts, adjust your date range and filters.'}/></span>
    );
}

const POSTS_LIMIT = 4;

const CSV_METRIC_OVERRIDES = [
    ['published_at'],
    ['company_name'],
    ['media_type', 'post_type'],
    ['message', 'post'],
    ['post_url'],
    ['likes'],
    ['comments'],
    ['shares'],
    ['engagement_total'],
    ['engagement_rate_by_view'],
    ['video_views']
];

function getCsvConfiguration(metricRequests) {
    const metricRequestValues = _.values(metricRequests);
    return {
        headerNameOverrides: _.map(CSV_METRIC_OVERRIDES, _.last),
        metricGroups: [
            _.reduce(_.map(CSV_METRIC_OVERRIDES, _.first), (memo, measure) => (
                memo.concat(
                    _.find(metricRequestValues, {
                        measure
                    })
                )
            ), [])
        ]
    };
}

export default function Posts({appSpecificExportConfig, instanceId, pdfExportConfig}) {

    const landscape = useContext(LandscapeContext);

    const {
        t
    } = useTranslation('apps/insights/components/Posts');

    const {
        t: tMetrics
    } = useTranslation('metrics');

    const getTikTokInsightsResource = createTikTokPostResourceLocator(tMetrics);

    const companyId = useCurationConfigValue(instanceId, 'companyId');
    const dateRanges = useComputedCurationConfigValue(instanceId, 'dateRanges');
    const searchTerm = useCurationConfigValue(instanceId, 'searchTerm');
    const timezone = useCurationConfigValue(instanceId, 'timezone');

    const [sortMetricName, setSortMetricName] = useCurationConfigState(instanceId, 'sortMetricName');
    const [sortOrder, setSortOrder] = useCurationConfigState(instanceId, 'sortOrder');

    const title = t('title');

    const focusedInstanceId = useGetFocusedInstanceIdValue();

    let limit = POSTS_LIMIT;
    if (focusedInstanceId === instanceId) {
        limit = null;
    }

    const isExporting = useIsExporting();

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title,
            subtitle: appSpecificExportConfig.timePeriodStr
        }
    });

    const createTikTokPrivatePostMetric = useCallback((measure) => {
        const metric = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
            dimensions: ['post_id'],
            measure: measure,
            filters: {
                company: {
                    onlyCompanyIds: [companyId],
                    landscapeId: landscape.id
                },
                timePeriod: {
                    startDate: dateRanges.main.start,
                    endDate: dateRanges.main.end,
                    timezone: timezone
                }
            }
        };

        if (searchTerm) {
            metric.filters.publicPost = {
                searchTerm: searchTerm
            };
        }

        return metric;
    }, [companyId, landscape.id, dateRanges.main.start, dateRanges.main.end, searchTerm, timezone]);

    function determineSortIndicator(metricName) {
        if (metricName === sortMetricName) {
            return sortOrder === 'desc' ? MetricTableHeader.SortIndicator.SORT_DOWN : MetricTableHeader.SortIndicator.SORT_UP;
        }
    }

    function onHeaderClick(metricName) {
        if (metricName === sortMetricName) {
            setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
            setSortMetricName(metricName);
            setSortOrder('asc');
        }
    }

    const columns = [
        {
            name: getTikTokInsightsResource('published_at', 'name'),
            description: getTikTokInsightsResource('published_at', 'description'),
            component: PublishedAtCell,
            data: {
                timezone: timezone
            },
            metrics: {
                published_at: createTikTokPrivatePostMetric('published_at')
            },
            sortKey: 'published_at'
        },
        {
            name: getTikTokInsightsResource('message', 'name'),
            description: getTikTokInsightsResource('message', 'description'),
            component: PostCell,
            data: {
                companyId
            },
            metrics: {
                comments: createTikTokPrivatePostMetric('comments'),
                company_name: createTikTokPrivatePostMetric('company_name'),
                engagement_rate_by_presence_reach: createTikTokPrivatePostMetric('engagement_rate_by_presence_reach'),
                likes: createTikTokPrivatePostMetric('likes'),
                media_type: createTikTokPrivatePostMetric('media_type'),
                media_url_persisted: createTikTokPrivatePostMetric('media_url_persisted'),
                message: createTikTokPrivatePostMetric('message'),
                post_url: createTikTokPrivatePostMetric('post_url'),
                presence_icon_url: createTikTokPrivatePostMetric('presence_icon_url'),
                reach: createTikTokPrivatePostMetric('reach'),
                shares: createTikTokPrivatePostMetric('shares')
            }
        },
        {
            name: getTikTokInsightsResource('engagement_total', 'name'),
            description: getTikTokInsightsResource('engagement_total', 'description'),
            alignment: MetricTableHeader.Alignment.RIGHT,
            component: MetricCell,
            metrics: {
                engagement_total: createTikTokPrivatePostMetric('engagement_total')
            },
            sortKey: 'engagement_total'
        },
        {
            name: getTikTokInsightsResource('engagement_rate_by_view', 'name'),
            description: getTikTokInsightsResource('engagement_rate_by_view', 'description'),
            alignment: MetricTableHeader.Alignment.RIGHT,
            component: MetricCell,
            metrics: {
                engagement_rate_by_view: createTikTokPrivatePostMetric('engagement_rate_by_view')
            },
            sortKey: 'engagement_rate_by_view'
        },
        {
            name: getTikTokInsightsResource('video_views', 'name'),
            description: getTikTokInsightsResource('video_views', 'description'),
            alignment: MetricTableHeader.Alignment.RIGHT,
            component: MetricCell,
            metrics: {
                video_views: createTikTokPrivatePostMetric('video_views')
            },
            sortKey: 'video_views'
        }
    ];

    const metricRequests = _.reduce(columns, (memo, column) => {
        return _.reduce(column.metrics, (memo, config, key) => (
            _.set(memo, `${column.name}-${key}`, config)
        ), memo);
    }, {});

    if (sortMetricName) {
        const sortMetricDefinition = _.find(metricRequests, (value, key) => key.indexOf(sortMetricName) >= 0);

        if (sortMetricDefinition) {
            const ordering = _.cloneDeep(sortMetricDefinition);
            delete ordering.filters?.ordering;
            ordering.sortOrder = sortOrder;

            _.each(metricRequests, (request) => {
                request.filters.ordering = {
                    sort: [ordering]
                };

                if (_.isFinite(limit)) {
                    request.filters.ordering.limit = limit;
                }
            });
        }
    }

    useSetExportConfig(instanceId, {
        title,
        supportedExportFormats: _.values(RivalIQ.enums.exportFormat),
        supportedExportTypes: _.values(RivalIQ.enums.exportType),
        csvExportConfig: _.defaults({
            type: 'dataSourceMetricsGenerator'
        }, getCsvConfiguration(metricRequests)),
        pdfExportConfig,
        appSpecificExportConfig
    });

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [postModalData, setPostModalData] = useState({});

    function onRowClick(row) {
        setPostModalData(row);
        setIsModalVisible(true);
    }

    let footerMetricRequests = {};
    if (!isExporting && instanceId === focusedInstanceId) {
        footerMetricRequests = {
            received: {
                dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
                dimensions: ['post_id'],
                measure: 'published_at',
                filters: {
                    company: {
                        onlyCompanyIds: [companyId],
                        landscapeId: landscape.id
                    },
                    publicPost: {
                        searchTerm: searchTerm
                    },
                    timePeriod: {
                        startDate: dateRanges.main.start,
                        endDate: dateRanges.main.end,
                        timezone
                    }
                }
            },
            total: {
                dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.TIKTOK_PRIVATE_POST_DATA,
                dimensions: [],
                measure: 'distinctCount(post_id)',
                filters: {
                    company: {
                        onlyCompanyIds: [companyId],
                        landscapeId: landscape.id
                    },
                    publicPost: {
                        searchTerm: searchTerm
                    },
                    timePeriod: {
                        startDate: dateRanges.main.start,
                        endDate: dateRanges.main.end,
                        timezone
                    }
                }
            }
        };
    }

    const {isLoading, error, data: footerMetricResults} = useMetrics(footerMetricRequests);
    let footerContextView = null;
    if (!_.isEmpty(footerMetricRequests) && !isLoading && !error) {
        footerContextView = (
            <PostsFooterContext metrics={footerMetricResults} />
        );
    }

    return (
        <Panel
            exportSubtitle={appSpecificExportConfig.timePeriodStr}
            footerContextView={footerContextView}
            instanceId={instanceId}
            title={title}
            description={t('description')}
            showViewMore={true}
        >
            <MetricLoadingView
                instanceId={instanceId}
                metricRequests={metricRequests}>
                {
                    (metricResults) => {
                        const thead = _.map(columns, ({alignment, description, name, sortKey}) => {
                            const props = {
                                alignment,
                                description,
                                key: name,
                                title: name
                            };

                            if (sortKey) {
                                props.sortIndicator = determineSortIndicator(sortKey);
                                props.onClick = () => onHeaderClick(sortKey);
                            }

                            return (
                                <MetricTableHeader {...props}/>
                            );
                        });

                        const tbody = _.map(
                            _.zip(
                                ..._.map(columns, (column) => {
                                    const prefix = `${column.name}-`;
                                    const columnMetricResults = _.reduce(metricResults, (memo, value, scopedKey) => {
                                        if (scopedKey.indexOf(prefix) >= 0) {
                                            return _.set(memo, scopedKey.replace(prefix, ''), value);
                                        }
                                        return memo;
                                    }, {});

                                    return _.zip(
                                        ..._.map(columnMetricResults, (result, key) => {
                                            return _.map(result.data, (datum) => {
                                                const columnResult = _.defaults({
                                                    data: [datum]
                                                }, result);

                                                return {
                                                    [key]: _.extend(datum, {
                                                        _metric: getMetricData(result.request, columnResult)
                                                    })
                                                };
                                            });
                                        })
                                    );
                                })
                            ),
                            (data) => {
                                const cells = _.map(data, (datum, i) => {
                                    const column = columns[i];

                                    const cellData = _.reduce(datum, (memo, obj) => (
                                        _.extend(memo, obj)
                                    ), {});

                                    const props = {
                                        ...column.data,
                                        data: (_.keys(cellData).length !== 1 ? cellData : _.first(_.values(cellData)))
                                    };

                                    return (
                                        <column.component {...props} />
                                    );
                                });

                                const rowData = _.reduce(_.flatten(data), (memo, obj) => _.extend(memo, obj), {});

                                return (
                                    <tr
                                        className='tlw-cursor-pointer tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-200 hover_tlw-bg-blue-50'
                                        onClick={() => onRowClick(rowData)}
                                    >
                                        {cells}
                                    </tr>
                                );
                            }
                        );

                        return (
                            <article>
                                <table className='tlw-w-full'>
                                    <thead>
                                        <tr className='tlw-bg-gray-150'>
                                            {thead}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tbody}
                                    </tbody>
                                </table>
                                <PostDetailsModal
                                    companyId={companyId}
                                    metricResults={postModalData}
                                    isModalVisible={isModalVisible}
                                    onModalDismissed={() => setIsModalVisible(false)}
                                    timezone={timezone}
                                />
                            </article>
                        );
                    }
                }
            </MetricLoadingView>
        </Panel>
    );
}
